import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button as MuiButton, Grid, IconButton, Stack } from '@mui/material';

import { IcDeleteTrash } from '@/assets/images';
import { FormElementProps, RenderFormField } from '@/components/FormElements';
import { Button } from '@/components/TailwindComponents';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { AddressStatus, IAddressForm } from '@/models/address';
import { colors } from '@/theme/variables';

import { AddressFormProps } from './AddressForm';
import { deactivateAllAddresses, hasActiveAddress } from './transientHelpers';

interface AddressFieldType extends IAddressForm {
  id: string;
  status: AddressStatus;
}

// Primary address fields that uniquely identify an address
// Only show verified badge if these key fields match the scanned values
const PRIMARY_ADDRESS_FIELDS = [
  'addresses.0.addressNumber',
  'addresses.0.completeStreetName',
];

const DEFAULT_ADDRESS = {
  status: AddressStatus.INACTIVE,
};

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    '_blank',
  );
}

export interface MultiAddressFormFieldProps
  extends Omit<AddressFormProps, 'name' | 'onChange'>,
    FormElementProps {
  handleChange?: (name: string, value: string) => void;
  isMasterNameField?: boolean;
  showMunicipalityTypeInput?: boolean;
  disabled?: boolean;
  /** Optional function to check if a field is verified (from VerifiedFieldsContext) */
  isFieldVerified?: (fieldName: string) => boolean;
}

export const MultiAddressForm: FC<MultiAddressFormFieldProps> = (props) => {
  const {
    rules,
    isFieldVerified,
    handleChange,
    disabled = false,
    showMunicipalityTypeInput = false,
    ...rest
  } = props;

  const { fields, remove, replace } = useFieldArray({
    name: 'addresses',
  });

  const { watch, setValue, getValues } = useFormContext();

  const addresses = watch('addresses');
  const address = watch('address');
  const transient = watch('transient') as boolean | undefined;

  const replaceRef = useRef(replace);
  useEffect(() => {
    replaceRef.current = replace;
  });

  const [explicitlyActiveIndex, setExplicitlyActiveIndex] = useState<
    number | null
  >(null);
  const hasInitializedActiveIndex = useRef(false);

  const hasVerifiedAddressFields = useMemo(() => {
    if (!isFieldVerified) return false;
    // Only show verified badge if ALL primary address fields match scanned values
    // This ensures the badge disappears when user selects a different address
    return PRIMARY_ADDRESS_FIELDS.every((field) => isFieldVerified(field));
  }, [isFieldVerified]);

  useEffect(() => {
    if (!addresses || addresses.length === 0) {
      const initialAddress = address
        ? { ...address, status: AddressStatus.INACTIVE }
        : DEFAULT_ADDRESS;
      setValue('addresses', [initialAddress], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [addresses, address, setValue]);

  useEffect(() => {
    if (hasInitializedActiveIndex.current) return;
    if (addresses && addresses.length > 0) {
      hasInitializedActiveIndex.current = true;
      const existingActiveIndex = (addresses as AddressFieldType[]).findIndex(
        (addr) => addr?.status === AddressStatus.ACTIVE,
      );
      if (existingActiveIndex !== -1) {
        setExplicitlyActiveIndex(existingActiveIndex);
        const hasMultipleActive =
          (addresses as AddressFieldType[]).filter(
            (addr) => addr?.status === AddressStatus.ACTIVE,
          ).length > 1;
        if (hasMultipleActive) {
          const normalizedAddresses = (addresses as AddressFieldType[]).map(
            (addr, idx) => ({
              ...addr,
              status:
                idx === existingActiveIndex
                  ? AddressStatus.ACTIVE
                  : AddressStatus.INACTIVE,
            }),
          );
          replace(normalizedAddresses);
        }
      }
    }
  }, [addresses, replace]);

  useEffect(() => {
    if (!transient) return;
    const current = (getValues('addresses') || []) as AddressFieldType[];
    if (current.length === 0) return;
    if (!hasActiveAddress(current)) return;
    replaceRef.current(deactivateAllAddresses(current));
    setExplicitlyActiveIndex(null);
  }, [transient, getValues]);

  const handleAddAddress = () => {
    const currentAddresses = (watch('addresses') || []) as AddressFieldType[];
    const updatedAddresses = [...currentAddresses];
    updatedAddresses.push({
      status: AddressStatus.INACTIVE,
    } as AddressFieldType);
    replace(updatedAddresses);
  };

  const handleSetActiveAddress = (index: number) => {
    setExplicitlyActiveIndex(index);

    const currentAddresses = (watch('addresses') || []) as AddressFieldType[];
    const updatedAddresses = currentAddresses.map((addressForm, idx) => ({
      ...addressForm,
      status: idx === index ? AddressStatus.ACTIVE : AddressStatus.INACTIVE,
    }));

    replace(updatedAddresses);
  };

  const handleRemoveAddress = (index: number) => {
    if (explicitlyActiveIndex !== null) {
      if (index === explicitlyActiveIndex) {
        setExplicitlyActiveIndex(null);
      } else if (index < explicitlyActiveIndex) {
        setExplicitlyActiveIndex(explicitlyActiveIndex - 1);
      }
    }
    remove(index);
  };

  const handleMapsClick = (e: React.MouseEvent, addressData: any) => {
    e.stopPropagation();
    const addressParts = [
      addressData.addressNumber,
      addressData.completeStreetName,
      addressData.placeName,
      addressData.state,
      addressData.zipcode,
      addressData.country,
    ].filter(Boolean);

    const fullAddress = addressParts.join(', ');

    if (fullAddress) {
      openInGoogleMaps(fullAddress);
    }
  };

  const getAddressString = (addressData: any): string => {
    if (!addressData) return '';
    const addressParts = [
      addressData.addressNumber,
      addressData.completeStreetName,
      addressData.placeName,
      addressData.state,
      addressData.zipcode,
      addressData.country,
    ].filter(Boolean);

    return addressParts.join(', ');
  };

  const effectiveDisabled = disabled || !!transient;

  return (
    <Stack display="flex" width="100%">
      {fields.map((field, index) => {
        const addressData = watch(`addresses.${index}`);
        const hasAddress = getAddressString(addressData).length > 0;

        const isActive = hasAddress && index === explicitlyActiveIndex;

        return (
          <AddressRow
            key={field.id}
            field={field}
            index={index}
            isActive={isActive}
            hasAddress={hasAddress}
            addressData={addressData}
            fieldsLength={fields.length}
            handleSetActiveAddress={handleSetActiveAddress}
            handleMapsClick={handleMapsClick}
            remove={handleRemoveAddress}
            rules={rules}
            handleChange={handleChange}
            showMunicipalityTypeInput={showMunicipalityTypeInput}
            disabled={effectiveDisabled}
            rest={rest}
            hasVerifiedAddressFields={hasVerifiedAddressFields}
          />
        );
      })}
      <Button
        color="error"
        type="button"
        size="sm"
        variant="secondary"
        className="w-fit text-sm"
        onClick={handleAddAddress}
        disabled={effectiveDisabled}
      >
        + Add new address
      </Button>
    </Stack>
  );
};

interface AddressRowProps {
  field: any;
  index: number;
  isActive: boolean;
  hasAddress: boolean;
  addressData: any;
  fieldsLength: number;
  handleSetActiveAddress: (index: number) => void;
  handleMapsClick: (e: React.MouseEvent, addressData: any) => void;
  remove: (index: number) => void;
  rules?: any;
  handleChange?: (name: string, value: string) => void;
  showMunicipalityTypeInput: boolean;
  disabled: boolean;
  hasVerifiedAddressFields: boolean;
  rest: any;
}

const AddressRow: FC<AddressRowProps> = ({
  field,
  index,
  isActive,
  hasAddress,
  fieldsLength,
  handleSetActiveAddress,
  remove,
  rules,
  handleChange,
  showMunicipalityTypeInput,
  disabled,
  hasVerifiedAddressFields,
  rest,
}) => {
  return (
    <Grid container spacing={2} key={field.id} alignItems="flex-start">
      <Grid item xs={8}>
        <div className="flex items-start w-full">
          <div className="flex-1">
            <RenderFormField
              {...rest}
              type="address"
              name={`addresses.${index}`}
              disabled={disabled}
              rules={rules}
              handleChange={handleChange}
              showMunicipalityTypeInput={showMunicipalityTypeInput}
            />
          </div>
          {index === 0 && hasVerifiedAddressFields && (
            <VerifiedBadge
              tooltip="Address verified by state ID"
              className="mt-7 ml-1"
            />
          )}
        </div>
      </Grid>
      <Grid
        item
        xs={4}
        style={{
          display: 'flex',
          marginTop: '22px',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <MuiButton
          variant="contained"
          color="error"
          size="small"
          disabled={disabled || isActive || !hasAddress}
          onClick={() => handleSetActiveAddress(index)}
        >
          {isActive ? 'Active' : 'Set as active'}
        </MuiButton>

        <IconButton
          onClick={() => remove(index)}
          disabled={disabled || fieldsLength === 1 || !hasAddress}
          sx={{
            color: colors.error.main,
          }}
        >
          <IcDeleteTrash />
        </IconButton>
      </Grid>
    </Grid>
  );
};
